import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  getBrandsCatalogue,
  getCategoriesByBrandCatalogue,
  getSkusByBrandCategoryCatalogue,
  getArticlesByBrandCategoryCatalogue,
} from "../../../api/Services";
import {
  hasPermission,
  privilegesEnum,
} from "../../../helpers/PermissionHelper";
import { getOrganizationId } from "../../../api/Helper";

import { Button, Dropdown, Menu, Select, Spin, Table } from "antd";
import { MoreOutlined, RedoOutlined, SendOutlined } from "@ant-design/icons";

import { useNavigate } from "react-router-dom";
import styles from "./Catalogue.module.scss";
import hardcodedData from "./hardcodedData.json";

enum filterSkus {
  all = "all",
  active = "active",
  inactive = "inactive",
}

const Catalogue = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [data, setData] = useState<any[]>([]);
  const [loadingArticles, setLoadingArticles] = useState(false);
  const [totalSize, setTotalSize] = useState(0);
  const [actualPage, setActualPage] = useState(0);
  const [expandedRows, setExpandedRows] = useState<string[]>([]);
  const [expandedRowData, setExpandedRowData] = useState<{
    [key: string]: any[];
  }>({});
  const [loadingExpandedRow, setLoadingExpandedRow] = useState<{
    [key: string]: boolean;
  }>({});
  const [expandedCategories, setExpandedCategories] = useState<string[]>([]);
  const [expandedCategoryData, setExpandedCategoryData] = useState<{
    [key: string]: any[];
  }>({});
  const [loadingCategoryRow, setLoadingCategoryRow] = useState<{
    [key: string]: boolean;
  }>({});
  const [categoryPagination, setCategoryPagination] = useState<{
    [key: string]: { current: number; total: number; pageSize: number };
  }>({});
  const [actualFilterSkus, setActualFilterSkus] = useState(filterSkus.all);
  const [isOrg5, setIsOrg5] = useState(false);

  useEffect(() => {
    const fetchOrg = async () => {
      const orgId = await getOrganizationId();
      setIsOrg5(parseInt(orgId) === 5);
    };
    fetchOrg();
  }, []);

  useEffect(() => {
    if (!isOrg5) {
      handleFetchArticles();
    } else {
      setLoadingArticles(true);
      setTimeout(() => {
        setData(hardcodedData.brands);
        setTotalSize(hardcodedData.brands.length);
        setLoadingArticles(false);
      }, 500);
    }
  }, [isOrg5]);

  const handlePagination = (page: number) => {
    if (!isOrg5) {
      handleFetchArticles(page);
    }
    setActualPage(page);
  };

  const handleFetchArticles = async (pagination = actualPage) => {
    setData([]);
    setLoadingArticles(true);

    const response = await getBrandsCatalogue(pagination);
    setData(response.brands);
    setTotalSize(response.totalElements);
    setLoadingArticles(false);
  };

  const getRowClassName = (_: any, index: number) => {
    return index % 2 === 0 ? styles.rowLight : styles.rowDark;
  };

  const columnsBrands = [
    {
      title: t("Common-Brand"),
      key: "name",
      render: (record: any) => record.brand.name,
    },
    {
      title: t("Common-Stock"),
      dataIndex: "totalStock",
      render: (value: any) => value || "-",
    },
    {
      title: t("Common-Supply"),
      dataIndex: "unsupply",
      render: (value: any) => value || "-",
    },
    {
      title: t("Common-Analysis"),
      dataIndex: "analysis",
      render: (value: any) => value || "-",
    },
    {
      title: t("Common-Strategy"),
      dataIndex: "strategy",
      render: (value: any) => value || "-",
    },
    {
      title: t("Common-InventoryLevelOptimal"),
      dataIndex: "inventory",
      render: (value: any) => value || "-",
    },
  ];

  const columnsCategories = [
    {
      title: t("Common-Category"),
      key: "name",
      render: (category: any) => {
        const parentsString = category?.parents
          ? category.parents.filter(Boolean).join(" - ")
          : "";
        const categoryName = category?.categoryName || "";

        return parentsString
          ? `${parentsString} - ${categoryName}`
          : categoryName;
      },
    },
    {
      title: t("Common-Stock"),
      dataIndex: "totalStock",
      render: (value: any) => value || "-",
    },
    {
      title: t("Common-Supply"),
      dataIndex: "unsupply",
      render: (value: any) => value || "-",
    },
    {
      title: t("Common-Analysis"),
      dataIndex: "analysis",
      render: (value: any) => value || "-",
    },
    {
      title: t("Common-Strategy"),
      dataIndex: "strategy",
      render: (value: any) => value || "-",
    },
    {
      title: t("Common-InventoryLevelOptimal"),
      dataIndex: "inventory",
      render: (value: any) => value || "-",
    },
  ];

  const columnsArticles = [
    {
      title: t("Common-Article"),
      key: "article",
      render: (article: any) => (
        <a href={`/admin/articles/${article?.id || ""}/view`}>
          {article?.name || "-"}
        </a>
      ),
    },
    {
      title: t("Common-Stock"),
      dataIndex: "totalStockArticles",
      render: (value: any) => value || "-",
    },
    {
      title: t("Common-Supply"),
      dataIndex: "unsupply",
      render: (value: any) => value || "-",
    },
    {
      title: t("Common-Analysis"),
      dataIndex: "analysis",
      render: (value: any) => value || "-",
    },
    {
      title: t("Common-Strategy"),
      dataIndex: "strategy",
      render: (value: any) => value || "-",
    },
    {
      title: t("Common-InventoryLevelOptimal"),
      dataIndex: "inventory",
      render: (value: any) => value || "-",
    },
    {
      key: "actions",
      render: (record: any) => (
        <Dropdown
          overlay={
            <Menu>
              {hasPermission(privilegesEnum.WRITE_PURCHASE_ORDERS) && (
                <Menu.Item
                  key="edit"
                  onClick={() => {
                    navigate("/purchaseorders/create");
                  }}
                  icon={<SendOutlined />}
                >
                  {t("Common-AddPurchaseOrder")}
                </Menu.Item>
              )}
            </Menu>
          }
        >
          <Button
            onClick={(e) => e.preventDefault()}
            className={styles.actionButtonMore}
          >
            <MoreOutlined className={styles.actionIconMore} />
          </Button>
        </Dropdown>
      ),
    },
  ];

  const handleFetchCategories = async (brandId: number) => {
    setLoadingExpandedRow((prev) => ({ ...prev, [brandId]: true }));
    try {
      if (isOrg5) {
        const brand = hardcodedData.brands.find((b) => b.brand.id === brandId);
        setLoadingExpandedRow((prev) => ({ ...prev, [brandId]: false }));
        setExpandedRowData((prev) => ({
          ...prev,
          [brandId]: brand ? brand.categories : [],
        }));
      } else {
        const response = await getCategoriesByBrandCatalogue(brandId, 0);
        setLoadingExpandedRow((prev) => ({ ...prev, [brandId]: false }));
        setExpandedRowData((prev) => ({
          ...prev,
          [brandId]: response.categories,
        }));
      }
    } catch (error) {
      setLoadingExpandedRow((prev) => ({ ...prev, [brandId]: false }));
    }
  };

  const handleFetchArticlesByCategory = useCallback(
    async (brandId: number, categoryId: number, page: number = 0) => {
      setLoadingCategoryRow((prev) => ({ ...prev, [categoryId]: true }));
      try {
        if (isOrg5) {
          const brand = hardcodedData.brands.find(
            (b) => b.brand.id === brandId
          );
          const category = brand?.categories.find(
            (c) => c.categoryId === categoryId
          );
          setLoadingCategoryRow((prev) => ({ ...prev, [categoryId]: false }));
          setExpandedCategoryData((prev) => ({
            ...prev,
            [categoryId]: category ? category.skus : [], // Note: hardcoded data still uses skus structure
          }));
        } else {
          const response = await getArticlesByBrandCategoryCatalogue(
            brandId,
            categoryId,
            page,
            actualFilterSkus === filterSkus.active
              ? 0
              : actualFilterSkus === filterSkus.inactive
              ? 1
              : undefined
          );
          setExpandedCategoryData((prev) => ({
            ...prev,
            [categoryId]: response.articles,
          }));

          // Store pagination info
          setCategoryPagination((prev) => ({
            ...prev,
            [categoryId]: {
              current: page + 1,
              total: response.totalElements,
              pageSize: 10,
            },
          }));
        }
        setLoadingCategoryRow((prev) => ({ ...prev, [categoryId]: false }));
      } catch (error) {
        setLoadingCategoryRow((prev) => ({ ...prev, [categoryId]: false }));
      }
    },
    [actualFilterSkus, isOrg5]
  );

  const expandedCategoryRender = (brandId: string) => (categoryRecord: any) => {
    const articles = expandedCategoryData[categoryRecord.categoryId] || [];
    const isLoading = loadingCategoryRow[categoryRecord.categoryId];
    const pagination = categoryPagination[categoryRecord.categoryId];

    if (isLoading) {
      return <Spin />;
    }

    const handlePageChange = (page: number) => {
      handleFetchArticlesByCategory(
        parseInt(brandId), 
        categoryRecord.categoryId,
        page - 1
      );
    };

    return (
      <Table
        key={`articles-table-${categoryRecord.categoryId}-${
          pagination?.current || 1
        }`}
        columns={columnsArticles}
        dataSource={articles}
        pagination={
          pagination && pagination.total > 10
            ? {
                current: pagination.current,
                total: pagination.total,
                pageSize: pagination.pageSize,
                showSizeChanger: false,
                showQuickJumper: false,
                showTotal: (total: number, range: [number, number]) =>
                  `${t("Common-Page")} ${range[0]}-${range[1]} ${t(
                    "Common-Of"
                  )} ${total} ${t("Common-Articles")}`,
                onChange: handlePageChange,
              }
            : false
        }
        rowKey={(articleRecord) => articleRecord?.id || Math.random()}
      />
    );
  };

  const expandedRowRender = (record: any) => {
    const categories = expandedRowData[record.brand.id] || [];
    const isLoading = loadingExpandedRow[record.brand.id];

    if (isLoading) {
      return <Spin />;
    }

    return (
      <Table
        columns={columnsCategories}
        dataSource={categories}
        pagination={false}
        rowKey={(record) => record.categoryId}
        expandedRowRender={expandedCategoryRender(record.brand.id)}
        onExpand={(expanded, categoryRecord) => {
          if (expanded) {
            handleFetchArticlesByCategory(
              record.brand.id,
              categoryRecord.categoryId
            );
            setExpandedCategories([
              ...expandedCategories,
              categoryRecord.categoryId,
            ]);
          } else {
            setExpandedCategories(
              expandedCategories.filter(
                (id) => id !== categoryRecord.categoryId
              )
            );
          }
        }}
        expandedRowKeys={expandedCategories}
      />
    );
  };

  return (
    <div className={styles.wrapper}>
      <div className={styles.header}>
        <h1 className={styles.title}>{t("Common-Catalogue")}</h1>
      </div>
      <div className={styles.contentWrapper}>
        {hasPermission(privilegesEnum.READ_PURCHASE_ORDERS) && (
          <div className={styles.tableWrapper}>
            <div className={styles.headerTableWrapper}>
              <div className={styles.filtersWrapper}>
                <div className={styles.filterWrapper}>
                  <Select
                    className={styles.filterSelect}
                    allowClear
                    defaultValue={actualFilterSkus}
                    onChange={(value) => {
                      setExpandedCategories([]);
                      setActualFilterSkus(value);
                    }}
                  >
                    <Select.Option value={filterSkus.all} key={filterSkus.all}>
                      {t("Common-All")}
                    </Select.Option>
                    <Select.Option
                      value={filterSkus.active}
                      key={filterSkus.active}
                    >
                      {t("Common-Actives")}
                    </Select.Option>
                    <Select.Option
                      value={filterSkus.inactive}
                      key={filterSkus.inactive}
                    >
                      {t("Common-Inactives")}
                    </Select.Option>
                  </Select>
                </div>
              </div>
              <Button
                icon={<RedoOutlined />}
                onClick={() => {
                  if (isOrg5) {
                    setData(hardcodedData.brands);
                    setTotalSize(hardcodedData.brands.length);
                  } else {
                    handleFetchArticles();
                  }
                }}
              />
            </div>
            <Table
              size="small"
              className={styles.table}
              loading={loadingArticles}
              columns={columnsBrands}
              expandedRowRender={expandedRowRender}
              onExpand={(expanded, record) => {
                if (expanded) {
                  handleFetchCategories(record.brand.id);
                  setExpandedRows([...expandedRows, record.brand.id]);
                } else {
                  setExpandedRows(
                    expandedRows.filter((id) => id !== record.brand.id)
                  );
                }
              }}
              expandedRowKeys={expandedRows}
              dataSource={data}
              rowKey={(record) => record.brand.id}
              rowClassName={getRowClassName}
              pagination={{
                total: totalSize,
                showSizeChanger: false,
                onChange: handlePagination,
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default Catalogue;
